import { AuditLogFieldChange } from '../types';

export type AuditLogMetadata =
    | string
    | AuditLogFieldChange[]
    | Record<string, unknown>
    | null
    | undefined;

const FIELD_NAME_KEYS = ['fieldName', 'field_name', 'FieldName', 'name', 'key'] as const;
const OLD_VALUE_KEYS = ['oldValue', 'old_value', 'OldValue'] as const;
const NEW_VALUE_KEYS = ['newValue', 'new_value', 'NewValue', 'value'] as const;
const FIELD_CHANGE_KEYS = new Set<string>([
    ...FIELD_NAME_KEYS,
    ...OLD_VALUE_KEYS,
    ...NEW_VALUE_KEYS,
]);

function formatMetadataValue(value: unknown): string {
    if (value == null) return '';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function readFirstKey(record: Record<string, unknown>, keys: readonly string[]): unknown {
    for (const key of keys) {
        if (key in record) return record[key];
    }
    return undefined;
}

function readFieldName(record: Record<string, unknown>): string {
    const value = readFirstKey(record, FIELD_NAME_KEYS);
    return value == null ? '' : String(value);
}

function hasOldOrNewValue(record: Record<string, unknown>): boolean {
    return (
        readFirstKey(record, OLD_VALUE_KEYS) !== undefined ||
        readFirstKey(record, NEW_VALUE_KEYS) !== undefined
    );
}

function isFieldChangeRecord(record: Record<string, unknown>): boolean {
    return Boolean(readFieldName(record)) && hasOldOrNewValue(record);
}

function fieldChangeFromRecord(record: Record<string, unknown>): AuditLogFieldChange {
    return {
        fieldName: readFieldName(record),
        oldValue: formatMetadataValue(readFirstKey(record, OLD_VALUE_KEYS) ?? ''),
        newValue: formatMetadataValue(readFirstKey(record, NEW_VALUE_KEYS) ?? ''),
    };
}

function ancillaryEntries(record: Record<string, unknown>): AuditLogFieldChange[] {
    return Object.entries(record)
        .filter(([key]) => !FIELD_CHANGE_KEYS.has(key))
        .map(([key, value]) => ({
            fieldName: key,
            oldValue: '',
            newValue: formatMetadataValue(value),
        }));
}

function keyValueEntries(metadata: Record<string, unknown>): AuditLogFieldChange[] {
    return Object.entries(metadata).map(([key, value]) => ({
        fieldName: key,
        oldValue: '',
        newValue: formatMetadataValue(value),
    }));
}

/** Desembrulha metadata que pode vir como string JSON (inclusive duplamente codificada). */
export function parseAuditMetadata(metadata: AuditLogMetadata): unknown {
    let current: unknown = metadata;

    for (let depth = 0; depth < 5; depth += 1) {
        if (current == null) return null;
        if (typeof current !== 'string') return current;

        const trimmed = current.trim();
        if (!trimmed || trimmed === 'null') return null;

        try {
            current = JSON.parse(trimmed) as unknown;
        } catch {
            return trimmed;
        }
    }

    return current;
}

function flattenMetadataNode(node: unknown): AuditLogFieldChange[] {
    if (node == null) return [];

    if (Array.isArray(node)) {
        return node.flatMap((item) => flattenMetadataNode(item));
    }

    if (!isPlainObject(node)) {
        return [
            {
                fieldName: 'valor',
                oldValue: '',
                newValue: formatMetadataValue(node),
            },
        ];
    }

    if (isFieldChangeRecord(node)) {
        return [fieldChangeFromRecord(node), ...ancillaryEntries(node)];
    }

    return keyValueEntries(node);
}

/** Normaliza metadata da API para linhas exibíveis na tabela de alterações. */
export function normalizeAuditMetadata(metadata: AuditLogMetadata): AuditLogFieldChange[] {
    const parsed = parseAuditMetadata(metadata);
    if (parsed == null) return [];

    if (typeof parsed === 'string') {
        const trimmed = parsed.trim();
        if (!trimmed) return [];
        return [
            {
                fieldName: 'metadata',
                oldValue: '',
                newValue: trimmed,
            },
        ];
    }

    return flattenMetadataNode(parsed);
}

/** Indica se o log possui metadata com conteúdo exibível. */
export function hasAuditMetadata(metadata: AuditLogMetadata): boolean {
    if (metadata == null) return false;

    if (typeof metadata === 'string') {
        const trimmed = metadata.trim();
        if (!trimmed || trimmed === 'null') return false;
        return normalizeAuditMetadata(metadata).length > 0 || trimmed.length > 0;
    }

    if (Array.isArray(metadata)) return metadata.length > 0;

    if (typeof metadata === 'object') return Object.keys(metadata).length > 0;

    return false;
}

/** Texto bruto para exibição quando o formato não é de alterações de campo. */
export function formatAuditMetadataRaw(metadata: AuditLogMetadata): string {
    const parsed = parseAuditMetadata(metadata);
    if (parsed == null) return '';

    if (typeof parsed === 'string') return parsed;

    return JSON.stringify(parsed, null, 2);
}
